import { defineStore } from "pinia";
import { AdminCompaniesApi, CompaniesApi, MicrosoftApi, SsoProvidersApi } from "@simbelapp/order-sdk";
import type {
  AddSsoProviderMetadatasResponse,
  CompanySsoProviderResponse,
  CreateCompanyDto,
  UpdateCompanyDto,
} from "@simbelapp/order-sdk";
import { useAuthStore } from "../auth/auth.store";
import type {
  ICreateCompanyStore,
  ICreateCompanyPayload,
  ICompanyOutlookSnapshot,
} from "~/utils/interfaces/companies-interfaces";
import { CompanyIdentificationModeEnum, CompanyAuthStrategy, CompanyLanguage } from "~/utils/enums/companies.enums";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useFormats } from "~/composables/utils/useFormat";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { CurrencyEnum } from "~/utils/enums/currency.enums";
import { createSseParser } from "~/utils/helpers/auth/sse";
import {
  assertAuthSessionCurrent,
  consumeSessionBoundStream,
  createAuthenticatedFetch,
} from "~/utils/helpers/auth/transport";
import { DEFAULT_TIMEZONE } from "~/utils/helpers/timezones";

const OUTLOOK_INCOMPLETE_ERROR = "Vous devez remplir les 3 champs concernant l’intégration Outlook";

/** Order-insensitive comparison: the checkboxes append and remove, so the array order drifts. */
function sameScheduleStatusTypes(
  left: ICompanyOutlookSnapshot["scheduleStatusTypes"],
  right: ICompanyOutlookSnapshot["scheduleStatusTypes"],
): boolean {
  const a = [...(left ?? [])].sort();
  const b = [...(right ?? [])].sort();

  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * Snapshot of the Outlook block. The status array is copied because the checkboxes push into it in
 * place, so sharing the reference would move the snapshot along with the edit it has to detect.
 */
function outlookSnapshotOf(form: ICreateCompanyPayload): ICompanyOutlookSnapshot {
  return {
    clientId: form.clientId,
    tenantId: form.tenantId,
    scheduleStatusTypes: form.scheduleStatusTypes ? [...form.scheduleStatusTypes] : form.scheduleStatusTypes,
  };
}

/** Whether the Outlook block moved since it was read — a typed secret always counts as a change. */
function hasOutlookChanges(form: ICreateCompanyPayload, snapshot: ICompanyOutlookSnapshot | null): boolean {
  if (form.clientSecret) return true;

  return (
    (form.clientId ?? null) !== (snapshot?.clientId ?? null) ||
    (form.tenantId ?? null) !== (snapshot?.tenantId ?? null) ||
    !sameScheduleStatusTypes(form.scheduleStatusTypes, snapshot?.scheduleStatusTypes)
  );
}

/**
 * Build the company body for the create/update endpoints. Outlook & request-form
 * fields are staged on company but persist via their own endpoints, so they
 * are intentionally excluded here. startSyncDate is a Date in the form, ISO on the wire.
 */
function buildCompanyBody(
  form: ICreateCompanyPayload,
  toDateISOString: (date: Date | string) => string,
  isUpdate: boolean,
) {
  const emailSenderName = form.emailSenderName?.trim() ?? "";

  return {
    name: form.name,
    welcomeMessage: form.welcomeMessage,
    managerValidation: form.managerValidation,
    identificationMode: form.identificationMode,
    isMainEntity: form.isMainEntity,
    javeloId: form.javeloId,
    startSyncDate: form.startSyncDate ? toDateISOString(form.startSyncDate) : undefined,
    notifications: form.notifications,
    authStrategy: form.authStrategy,
    hideCatalogPrices: form.hideCatalogPrices,
    language: form.language,
    n2Managers: form.n2Managers,
    ocrSignAndSheet: form.ocrSignAndSheet,
    learningPaths: form.learningPaths,
    priorityConfirmationByGroupAdmin: form.priorityConfirmationByGroupAdmin,
    groupAdminSalaryCostsVisible: form.groupAdminSalaryCostsVisible,
    aiCompanionMcpEnabled: form.aiCompanionMcpEnabled,
    allowPreprodEmails: form.allowPreprodEmails,
    companyType: form.companyType,
    currency: form.currency,
    defaultTimezone: form.defaultTimezone ?? undefined,
    // A blank name means two different things: omitted, a new company falls back to its own name,
    // while an empty string on update clears the setting back to the platform name.
    ...(isUpdate || emailSenderName ? { emailSenderName } : {}),
  };
}

export const useCreateCompaniesStore = defineStore("useCreateCompaniesStore", {
  state: (): ICreateCompanyStore => {
    return {
      company: {
        name: "",
        managerValidation: false,
        identificationMode: CompanyIdentificationModeEnum.EMAIL,
        isMainEntity: true,
        emailAdmin: "",
        authStrategy: CompanyAuthStrategy.LOGIN_PASSWORD,
        notifications: {
          newEmployeeRequest: true,
          refusedEmployeeRequest: true,
          waitingHrValidation: true,
          bookingToBeConfirmed: true,
          bookingConfirmed: true,
          bookingDatesUpdated: true,
          bookingCanceledEmployee: true,
          bookingCanceledManager: true,
        },
        emailSenderName: "",
        hideCatalogPrices: false,
        language: CompanyLanguage.FR,
        n2Managers: false,
        ocrSignAndSheet: false,
        learningPaths: false,
        priorityConfirmationByGroupAdmin: false,
        groupAdminSalaryCostsVisible: true,
        aiCompanionMcpEnabled: false,
        allowPreprodEmails: false,
        requestFormTemplateEnabled: false,
        requestFormTemplate: "",
        currency: CurrencyEnum.Eur,
        // Every platform starts on Europe/Paris.
        defaultTimezone: DEFAULT_TIMEZONE,
      },
      outlookSnapshot: null,
      logoUrl: null,
      logo: null,
      banner: null,
      ssoProvider: null,
      errors: {},
      showCreateModal: false,
      isLoadingCompany: false,
      isUpdate: false,
      showDeleteDashboardModal: false,
      modeEditBanner: false,
      duplicateProgress: null as string | null,
    };
  },
  actions: {
    /** The company's SAML provider, or null when it has none yet. */
    async fetchCompanySsoProvider(companyId: string): Promise<CompanySsoProviderResponse | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminCompaniesApi);
        return await apiInstance.getSSOProvider({ companyId });
      } catch {
        return null;
      }
    },

    async createCompanySsoProvider(companyId: string): Promise<CompanySsoProviderResponse | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminCompaniesApi);
        return await apiInstance.createSSOProvider({ companyId });
      } catch {
        return null;
      }
    },

    /**
     * Uploads the identity provider's SAML metadata XML. Until it lands, the provider has no
     * `loginUrl` / `logoutUrl` and the SSO login cannot redirect anywhere.
     */
    async addSsoProviderMetadatas(companyId: string, file: File): Promise<AddSsoProviderMetadatasResponse | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SsoProvidersApi);
        return await apiInstance.addSSOProviderMetadatas({ companyId, file });
      } catch {
        return null;
      }
    },

    async checkN2Managers(companyId: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
        const { n2Managers } = await apiInstance.checkN2ManagersActivated({ companyId: companyId });

        this.company.n2Managers = !!n2Managers;
        return !!n2Managers;
      } catch {
        useFeedback().error("Erreur lors de la vérification des managers N2", "small");
        return false;
      }
    },

    /**
     * Open the create company modal and reset the store state.
     */
    openCreateCompanyModal() {
      this.$reset();
      this.showCreateModal = true;
    },

    /**
     * Opens the modal on an existing company, in edit mode and loading from the start: the creation
     * form, whose « Créer » would POST a new company, never shows while the settings are on their way.
     */
    async openEditCompanyModal(companyId: string) {
      this.$reset();
      this.isUpdate = true;
      this.isLoadingCompany = true;
      this.showCreateModal = true;

      await this.fetchCompanySettings(companyId);
      this.isLoadingCompany = false;

      // The read failed and said so: an empty form for this company would only mislead.
      if (!this.company.companyId) this.closeCompanyModal();
    },

    closeCompanyModal() {
      this.showCreateModal = false;
      this.isUpdate = false;
    },

    /** Clears what the last save painted, so the next one is judged from scratch. */
    clearSaveErrors() {
      this.clearOutlookErrors();
      this.errors.company = "";
      this.errors.updateCompany = "";
    },

    /** Creates the company, then its Outlook, request-form, logo and banner settings. */
    async createCompany() {
      const { toDateISOString } = useFormats();

      if (!this.company?.welcomeMessage) {
        this.company.welcomeMessage = `Bienvenue sur votre académie de formation`;
      }

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminCompaniesApi);
        const company = await apiInstance.createCompany({
          createCompanyDto: {
            ...buildCompanyBody(this.company, toDateISOString, false),
            // Orders stores the admin's email as received, spaces included.
            emailAdmin: this.company.emailAdmin?.trim() ?? "",
          } as CreateCompanyDto,
        });

        if (company) {
          await this.setCalendarOutlookSettings(company.companyId);
          await this.saveRequestFormSettings(company.companyId);
          await this.updateCompanyLogo(company.companyId);
          if (this.banner instanceof File) {
            await this.convertCompanyBanner(this.banner);
            await this.updateCompanyBanner(company.companyId);
          }
        }
        return company;
      } catch (error: unknown) {
        this.errors.company = error instanceof Error ? error.message : "";
        useFeedback().error("Une erreur est survenue lors de la création de l'entreprise", "small");
        return undefined;
      }
    },

    /**
     * Convert the company logo to PNG format.
     * @param {File} logo - The logo file to convert.
     * @returns {Promise<void>}
     */
    async convertCompanyLogo(logo: File) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
        const response = await apiInstance.convertCompanyLogoRaw({
          file: logo,
        });
        const convertedImg = await response.raw.blob();
        if (convertedImg) {
          this.logo = new File([convertedImg], logo.name, { type: "image/png" });
        }
      } catch {
        useFeedback().error("Erreur lors de la conversion du logo", "small");
      }
    },

    /**
     * Convert the company banner to PNG format.
     * @param {File} banner - The banner file to convert.
     * @returns {Promise<void>}
     */
    async convertCompanyBanner(banner: File) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
        const response = await apiInstance.convertCompanyBannerRaw({
          file: banner,
        });
        const convertedImg = await response.raw.blob();
        if (convertedImg) {
          this.banner = new File([convertedImg], banner.name, { type: "image/png" });
        }
      } catch {
        useFeedback().error("Erreur lors de la conversion de la bannière", "small");
      }
    },

    /**
     * Update the company logo.
     * @param {string} companyId - The ID of the company.
     * @returns {Promise<void>}
     */
    async duplicateCompany(companyId: string, newCompanyName: string, emailDomain?: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminCompaniesApi);
        return await apiInstance.duplicateCompany({
          companyId,
          duplicateCompanyDto: { newCompanyName, emailDomain },
        });
      } catch {
        useFeedback().error("Erreur lors de la duplication de l'entreprise", "small");
        return undefined;
      }
    },

    async duplicateCompanyStream(
      companyId: string,
      newCompanyName: string,
      emailDomain?: string,
    ): Promise<{ newCompanyId: string }> {
      const config = useRuntimeConfig();
      const authStore = useAuthStore();
      const epoch = authStore.sessionEpoch;

      const baseUrl = config.public.ORDERS_URL;

      const abortController = new AbortController();
      const timeout = setTimeout(() => abortController.abort(), 240_000);

      try {
        const response = await createAuthenticatedFetch(authStore)(
          `${baseUrl}/admin/companies/${companyId}/duplicate/stream`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "text/event-stream",
            },
            body: JSON.stringify({ newCompanyName, emailDomain }),
            signal: abortController.signal,
          },
        );
        assertAuthSessionCurrent(authStore, epoch);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let newCompanyId = "";
        let streamError: Error | null = null;
        const parser = createSseParser({
          onEvent: ({ data }) => {
            const envelope = JSON.parse(data);
            const event = JSON.parse(envelope.data);

            if (event.type === "progress") {
              this.duplicateProgress = event.step;
            } else if (event.type === "complete") {
              newCompanyId = event.newCompanyId;
            } else if (event.type === "error") {
              streamError = new Error(event.message);
            }
          },
        });

        await consumeSessionBoundStream(authStore, epoch, reader, (value) => {
          parser.feed(decoder.decode(value, { stream: true }));
          if (streamError) throw streamError;
        });
        parser.feed(decoder.decode());
        parser.finish();
        assertAuthSessionCurrent(authStore, epoch);
        if (streamError) throw streamError;

        assertAuthSessionCurrent(authStore, epoch);
        this.duplicateProgress = null;
        return { newCompanyId };
      } finally {
        clearTimeout(timeout);
        this.duplicateProgress = null;
      }
    },

    async deleteCompany(companyId: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminCompaniesApi);
        return await apiInstance.deleteCompany({ companyId });
      } catch {
        useFeedback().error("Erreur lors de la suppression de l'entreprise", "small");
        return undefined;
      }
    },

    async updateCompanyAuthStrategy(companyId: string, authStrategy: string) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminCompaniesApi);
      return await apiInstance.updateAuthStrategy({
        companyId,
        updateCompanyAuthStrategyDto: { authStrategy },
      });
    },

    async updateCompanyLogo(companyId: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminCompaniesApi);
        const data = await apiInstance.updateCompanyLogo({
          file: this.logo ?? undefined,
          companyId,
        });

        if (data.url) {
          this.logoUrl = data.url;
        }
      } catch {
        useFeedback().error("Erreur lors de la mise à jour du logo", "small");
      }
    },

    /**
     * Update the company banner.
     * @param {string} companyId - The ID of the company.
     * @returns {Promise<void>}
     */
    async updateCompanyBanner(companyId: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminCompaniesApi);
        await apiInstance.updateCompanyBanner({
          file: this.banner instanceof File ? this.banner : undefined,
          companyId,
        });
      } catch {
        useFeedback().error("Erreur lors de la mise à jour de la bannière", "small");
      }
    },

    /**
     * Fetch the company settings.
     * @param {string} companyId - The ID of the company.
     * @returns {Promise<void>}
     */
    async fetchCompanySettings(companyId: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
        // CompanySettings now carries hideCatalogPrices, language, logo, bannerUrl, ocrSignAndSheet,
        // learningPaths, companyType & typed notifications — one call replaces the old settings + hidden-prices + language trio.
        const settings = await apiInstance.getCompanySettings({ companyId: companyId });
        const { logo, bannerUrl, startSyncDate, notifications, ...company } = settings;

        this.company = {
          ...this.company,
          ...company,
          // getCompanySettings can return null/undefined notifications; keep a valid object so the
          // template's company.notifications.* bindings never dereference undefined.
          notifications: notifications ?? this.company.notifications,
          emailSenderName: company.emailSenderName ?? "",
          startSyncDate: startSyncDate ? new Date(startSyncDate) : undefined,
          defaultTimezone: company.defaultTimezone ?? DEFAULT_TIMEZONE,
        } as ICreateCompanyPayload;
        this.logoUrl = logo ?? null;
        this.banner = bannerUrl ?? null;

        await Promise.all([this.fetchCalendarOutlookSettings(companyId), this.fetchRequestFormSettings(companyId)]);
      } catch {
        useFeedback().error("Erreur lors de la récupération des paramètres de l'entreprise", "small");
      }
    },

    /** Clear the Outlook field errors so the next save re-validates from scratch. */
    clearOutlookErrors() {
      this.errors.integrationOutlookClientSecret = "";
      this.errors.integrationOutlookClientId = "";
      this.errors.integrationOutlookTenantId = "";
    },

    /**
     * A new company has no stored secret, so its Outlook block is all three fields or none.
     * Paints the missing ones and returns true when the form is partial.
     */
    paintIncompleteOutlookTriplet(): boolean {
      const { clientId, clientSecret, tenantId } = this.company;
      const filled = [clientId, tenantId, clientSecret].filter(Boolean).length;

      if (filled === 0 || filled === 3) return false;

      if (!clientId) this.errors.integrationOutlookClientId = OUTLOOK_INCOMPLETE_ERROR;
      if (!tenantId) this.errors.integrationOutlookTenantId = OUTLOOK_INCOMPLETE_ERROR;
      if (!clientSecret) this.errors.integrationOutlookClientSecret = OUTLOOK_INCOMPLETE_ERROR;

      return true;
    },

    /**
     * On an existing company the secret is never read back, so the "three fields together" rule can
     * only be checked against a freshly typed one. Returns true when the form is partial.
     */
    paintOutlookIdsRequiredForSecret(): boolean {
      const { clientId, clientSecret, tenantId } = this.company;

      if (!clientSecret || (clientId && tenantId)) return false;

      if (!clientId) this.errors.integrationOutlookClientId = OUTLOOK_INCOMPLETE_ERROR;
      if (!tenantId) this.errors.integrationOutlookTenantId = OUTLOOK_INCOMPLETE_ERROR;

      return true;
    },

    /**
     * Fetch the Outlook calendar settings for a company.
     * @param {string} companyId - The ID of the company.
     * @returns {Promise<void>}
     */
    async fetchCalendarOutlookSettings(companyId: string) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, MicrosoftApi);
      try {
        const data = await apiInstance.findCompanyMicrosoftSettings({ companyId });

        this.company.clientId = data.clientId;
        this.company.tenantId = data.tenantId;
        this.company.scheduleStatusTypes = data.scheduleStatusTypes;
        // The secret never leaves the backend: the read only tells us whether one is stored.
        this.company.hasClientSecret = data.hasClientSecret;
        this.company.clientSecret = null;
      } catch {
        this.company.clientId = null;
        this.company.tenantId = null;
        this.company.scheduleStatusTypes = null;
        this.company.hasClientSecret = false;
        this.company.clientSecret = null;
      }

      this.outlookSnapshot = outlookSnapshotOf(this.company);
    },
    async fetchOcrSignAndSheetSettings(companyId: string) {
      const company = this.company;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
        const response = await apiInstance.getCompanyOcrSignAndSheet({ companyId });
        // Login loads this in the background; a company form may have opened since it started.
        if (this.company !== company) return;
        this.company.ocrSignAndSheet = response.ocrSignAndSheet;
      } catch {
        if (this.company !== company) return;
        useFeedback().error("Erreur lors de la récupération des paramètres OCR", "small");
      }
    },

    async fetchRequestFormSettings(companyId: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
        const response = await apiInstance.getRequestFormSettings({ companyId });
        this.company.requestFormTemplateEnabled = response.requestFormTemplateEnabled;
        this.company.requestFormTemplate = response.requestFormTemplate ?? "";
      } catch {
        this.company.requestFormTemplateEnabled = false;
        this.company.requestFormTemplate = "";
      }
    },

    async saveRequestFormSettings(companyId: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
        await apiInstance.updateRequestFormSettings({
          companyId,
          updateRequestFormSettingsDto: {
            requestFormTemplateEnabled: this.company.requestFormTemplateEnabled ?? false,
            requestFormTemplate: this.company.requestFormTemplate || null,
          },
        });
      } catch {
        const feedback = useFeedback();
        feedback.error("Erreur lors de la mise à jour du formulaire de demande", "small");
      }
    },
    /**
     * Set outlook calendar settings for a company.
     *
     * The endpoint rewrites the whole row from the payload and the read no longer returns the
     * secret, so saving with an empty one would blank it. We therefore only write when the block
     * actually changed, and refuse a change that carries no freshly typed secret.
     *
     * @param {string} companyId
     * @returns {Promise<boolean>} false when the caller must stop and let the admin fix the form.
     */
    async setCalendarOutlookSettings(companyId: string): Promise<boolean> {
      const { clientId, clientSecret, tenantId, scheduleStatusTypes } = this.company;

      if (!hasOutlookChanges(this.company, this.outlookSnapshot)) return true;

      if (!clientSecret) {
        this.errors.integrationOutlookClientSecret =
          "Saisissez à nouveau le Client Secret pour enregistrer l'intégration Outlook";
        return false;
      }

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, MicrosoftApi);
        await apiInstance.setCompanyMicrosoftSettings({
          companyId,
          setCompanyMicrosoftSettingsDto: {
            clientId: clientId || "",
            clientSecret,
            tenantId: tenantId || "",
            scheduleStatusTypes: scheduleStatusTypes ?? null,
          },
        });

        this.company.hasClientSecret = true;
        this.company.clientSecret = null;
        this.outlookSnapshot = outlookSnapshotOf(this.company);
        return true;
      } catch {
        useFeedback().error("Erreur lors de la configuration du calendrier Outlook", "small");
        return false;
      }
    },

    /** Saves the company. Resolves to false when the save was refused, its reason left in `errors`. */
    async updateCompany(companyId: string): Promise<boolean> {
      const { toDateISOString } = useFormats();

      try {
        // Outlook & request-form settings live on their own endpoints, not the company body.
        // Outlook goes first and on its own: running them together would persist the request-form
        // settings before a refusal could stop the save, leaving a partial write behind an error.
        const outlookSaved = await this.setCalendarOutlookSettings(companyId);

        // Refused Outlook write: keep the modal open on its error instead of closing on a success toast.
        if (!outlookSaved) return false;

        await this.saveRequestFormSettings(companyId);

        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminCompaniesApi);
        await apiInstance.updateCompany({
          companyId,
          updateCompanyDto: buildCompanyBody(this.company, toDateISOString, true) as UpdateCompanyDto,
        });

        if (this.logo instanceof File || (!this.logo && !this.logoUrl)) {
          await this.updateCompanyLogo(companyId);
        }
        return true;
      } catch (error: unknown) {
        this.errors.updateCompany = error instanceof Error ? error.message : "";
        useFeedback().error("Une erreur est survenue lors de la mise à jour de l'entreprise", "small");
        return false;
      }
    },
  },
});
